import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Heart, Waves, BookOpen, ShieldCheck } from "lucide-react";
import { isNativeApp } from "@/lib/native-app";
import { hapticSelection } from "@/lib/native-runtime";

const KEY = "rt_native_onboarding_v1";

const SLIDES = [
  {
    icon: Heart,
    title: "Welcome to Recovery Trauma™",
    body: "A survivor-led space for Healing Beyond Survival Mode™. Everything here moves at your pace — you choose what to open and when.",
  },
  {
    icon: Waves,
    title: "Calm your body first",
    body: "Breathing, grounding, calming sounds and guided meditations. One track plays at a time, and audio keeps going when your screen locks.",
  },
  {
    icon: BookOpen,
    title: "Learn and reflect",
    body: "Trauma-informed articles, self-reflection tools and private journaling that only you can see.",
  },
  {
    icon: ShieldCheck,
    title: "Your safety comes first",
    body: "Crisis support is always one tap away, and you can leave any exercise at any moment. Nothing here diagnoses or replaces professional care.",
  },
];

/**
 * First-run onboarding shown only inside the native app. Web visitors see the
 * website exactly as before.
 */
export function NativeOnboarding() {
  const [show, setShow] = useState(false);
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp()) return;
    try {
      if (!localStorage.getItem(KEY)) setShow(true);
    } catch {
      /* storage blocked — skip onboarding */
    }
  }, []);

  if (!show) return null;

  const finish = () => {
    try {
      localStorage.setItem(KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  const slide = SLIDES[index]!;
  const Icon = slide.icon;
  const last = index === SLIDES.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Recovery Trauma"
      className="fixed inset-0 z-[200] flex flex-col bg-background px-6"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 2rem)", paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
    >
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <span className="mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon className="h-9 w-9" aria-hidden="true" />
        </span>
        <h1 className="text-2xl font-semibold text-foreground">{slide.title}</h1>
        <p className="mt-4 max-w-sm text-base leading-relaxed text-muted-foreground">{slide.body}</p>
      </div>

      <div className="mb-6 flex justify-center gap-2" aria-hidden="true">
        {SLIDES.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-primary" : "w-1.5 bg-border"}`}
          />
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => {
            void hapticSelection();
            if (last) {
              finish();
              navigate({ to: "/my-healing-journey" });
            } else {
              setIndex((i) => i + 1);
            }
          }}
          className="min-h-[52px] w-full rounded-full bg-primary px-6 text-base font-medium text-primary-foreground"
        >
          {last ? "Start where you are" : "Continue"}
        </button>
        <button
          type="button"
          onClick={finish}
          className="min-h-[44px] w-full text-sm text-muted-foreground"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
